/* eslint-disable no-param-reassign, class-methods-use-this, react/no-unused-class-component-methods */
import {utils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {
  differenceInDays,
  differenceInSeconds,
  format,
  isValid,
  setMilliseconds,
} from 'date-fns';
import {isEmpty, isNil} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import DatePicker from 'react-datepicker';
import {defineMessages, FormattedMessage} from 'react-intl';

import styles from './GridDateRangeFilter.less';
import GridFilterWrapper from './GridFilterWrapper';
import {DynamicFilter} from 'src/js/lib/filtering/filters_definitions';
import {
  FILTER_TYPE_DATE_RANGE,
  FILTER_TYPE_DATE_ONLY_RANGE,
} from 'src/js/lib/filtering/filters_definitions_constants';

const I18N = defineMessages({
  FROM: {
    id: 'filter.date.from',
    defaultMessage: 'From',
    description: 'label for start date in date picker input',
  },
  TO: {
    id: 'filter.date.to',
    defaultMessage: 'To',
    description: 'label for end date in date picker input',
  },
  END_PLACEHOLDER: {
    id: 'filter.date.end.placeholder',
    defaultMessage: 'end date',
    description: 'placeholder for end date in date picker input',
  },
  START_PLACEHOLDER: {
    id: 'filter.date.start.placeholder',
    defaultMessage: 'start date',
    description: 'placeholder for start date in date picker input',
  },
  TODAY: {
    id: 'filter.date.today',
    defaultMessage: 'Today',
    description: 'text for today button in date picker input',
  },
  TOTAL_TEXT: {
    id: 'filter.date.totalText',
    defaultMessage: 'Total interval: {dateDifference} days',
    description: 'summary text for total interval',
  },
});

const STATIC_EMPTY_ARRAY = [];
const STATIC_EMPTY_OBJECT = {};

class GridDateRangeFilter extends React.PureComponent {
  static resetToGranularity(date, dateOnly) {
    let result = date;
    if (dateOnly) {
      result = utils.DateUtils.resetTime(result);
    } else {
      result = setMilliseconds(result, 0);
    }
    return result;
  }

  constructor(props, context) {
    super(props, context);

    const {valueGetter} = this.props;
    const start = new Date();
    const end = new Date();
    this.state = {
      // model that is not changed, unless the filter change is finally submitted
      loadedModel: undefined,
      // model that is changed with each filter change
      currentModel: GridDateRangeFilter.getFilter({start, end}, props),
      start,
      end,
      startOpen: false,
      endOpen: false,
      touched: false,
    };
    this.valueGetter = valueGetter;

    this.handleChangeStart = this.handleChangeStart.bind(this);
    this.handleChangeEnd = this.handleChangeEnd.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.removeFilter = this.removeFilter.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleInputClickStart = this.handleInputClickStart.bind(this);
    this.handleInputClickEnd = this.handleInputClickEnd.bind(this);
    this.isFilterEmpty = this.isFilterEmpty.bind(this);
  }

  static getFilter(state, props) {
    const {colDef, dateOnly} = props;
    let startDate = GridDateRangeFilter.getStartDate(state);
    let endDate = GridDateRangeFilter.getEndDate(state);
    let value;
    if (!isNil(startDate) && !isNil(endDate)) {
      if (dateOnly && isValid(startDate) && isValid(endDate)) {
        startDate = format(startDate, utils.DateUtils.ISO_DATE_ONLY_FORMAT);
        endDate = format(endDate, utils.DateUtils.ISO_DATE_ONLY_FORMAT);
      }
      value = [startDate, endDate];
    }
    const filterType = dateOnly
      ? FILTER_TYPE_DATE_ONLY_RANGE
      : FILTER_TYPE_DATE_RANGE;
    const DateRangeFilter = DynamicFilter.getFilterClass(filterType);
    return new DateRangeFilter({
      type: filterType,
      property: colDef.field,
      value,
    });
  }

  static getStartDate(state) {
    const {start} = state;
    return !isNil(start) ? new Date(start) : null;
  }

  static getEndDate(state) {
    const {end} = state;
    return !isNil(end) ? new Date(end) : null;
  }

  handleKeyDown(event) {
    if (!this.isFilterEmpty()) {
      if (event.key === 'Enter') {
        event.stopPropagation();
        this.onSubmit();
      }
    }
    return true;
  }

  handleInputClickStart() {
    this.setBlur(this.elemStart);
  }

  handleInputClickEnd() {
    this.setBlur(this.elemEnd);
  }

  handleChangeStart(value) {
    const {filterModifiedCallback} = this.props;
    const {start} = this.state;
    if (start !== value) {
      this.setState(
        {
          start: value,
          touched: true,
        },
        () => {
          this.setState(
            (state, props) => ({
              currentModel: GridDateRangeFilter.getFilter(state, props),
            }),
            () => {
              filterModifiedCallback();
            }
          );
        }
      );
    }
  }

  handleChangeEnd(value) {
    const {filterModifiedCallback} = this.props;
    const {end} = this.state;
    if (end !== value) {
      this.setState(
        {
          end: value,
          touched: true,
        },
        () => {
          this.setState(
            (state, props) => ({
              currentModel: GridDateRangeFilter.getFilter(state, props),
            }),
            () => {
              filterModifiedCallback();
            }
          );
        }
      );
    }
  }

  onSubmit() {
    const {filterChangedCallback} = this.props;
    const {start, end} = this.state;
    if (start && end && isValid(start) && isValid(end) && end < start) {
      return;
    }
    this.setState(
      (state) => ({
        loadedModel: state.currentModel,
        touched: true,
      }),
      () => {
        filterChangedCallback();
        if (this.hidePopUp) {
          this.hidePopUp();
        }
      }
    );
  }

  getDateDifference() {
    const {end, start} = this.state;
    // the closed interval (1.1.2018 - 1.1.2018 is 1 day)
    const diff =
      !isNil(start) && !isNil(end)
        ? differenceInDays(
            utils.DateUtils.resetTime(end),
            utils.DateUtils.resetTime(start)
          ) + 1
        : 0;
    return diff;
  }

  getModel() {
    const {currentModel, touched} = this.state;
    return touched ? currentModel : null;
  }

  setModel(model) {
    this.setState({
      start: model && model.getStartDate ? model.getStartDate() : new Date(),
      end: model && model.getEndDate ? model.getEndDate() : new Date(),
      touched: model && model.getStartDate && model.getEndDate,
      loadedModel: model || STATIC_EMPTY_OBJECT,
      currentModel: model || STATIC_EMPTY_OBJECT,
    });
  }

  setBlur(elem) {
    if (elem) {
      elem.setOpen(false, true);
    }
  }

  isFilterActive() {
    const {loadedModel} = this.state;
    return !isEmpty(loadedModel);
  }

  isFilterEmpty() {
    const {end, start} = this.state;
    return isNil(start) || isNil(end);
  }

  doesFilterPass(params) {
    const {dateOnly} = this.props;
    const {end, start} = this.state;
    const differenceFn = dateOnly ? differenceInDays : differenceInSeconds;
    const nodeDate = new Date(this.valueGetter(params.node));

    const isSameOrAfterStart =
      differenceFn(
        GridDateRangeFilter.resetToGranularity(nodeDate, dateOnly),
        GridDateRangeFilter.resetToGranularity(start, dateOnly)
      ) >= 0;
    const isSameOrBeforeEnd =
      differenceFn(
        GridDateRangeFilter.resetToGranularity(end, dateOnly),
        GridDateRangeFilter.resetToGranularity(nodeDate, dateOnly)
      ) >= 0;

    return this.isFilterEmpty() || (isSameOrAfterStart && isSameOrBeforeEnd);
  }

  afterGuiAttached(params) {
    this.focus();
    this.hidePopUp = params.hidePopup;
  }

  focus() {
    setTimeout(() => {
      if (this.elemStart && this.elemStart.input) {
        this.elemStart.input.focus();
      }
    });
  }

  removeFilter() {
    const {filterChangedCallback} = this.props;
    this.setState(
      {
        loadedModel: undefined,
        start: new Date(),
        end: new Date(),
        touched: false,
      },
      () => {
        this.setState(
          (state, props) => ({
            currentModel: GridDateRangeFilter.getFilter(state, props),
          }),
          () => {
            filterChangedCallback();
            if (this.hidePopUp) {
              this.hidePopUp();
            }
          }
        );
      }
    );
  }

  render() {
    const {currentModel, endOpen, loadedModel, startOpen} = this.state;
    const {colDef} = this.props;
    const filter = currentModel;
    const startDate = GridDateRangeFilter.getStartDate(this.state);
    const endDate = GridDateRangeFilter.getEndDate(this.state);
    const dateDifference = this.getDateDifference();

    return (
      <div className={styles.dlGridDateRangeFilter}>
        <GridFilterWrapper
          filter={filter}
          isActive={this.isFilterActive()}
          isEmpty={this.isFilterEmpty()}
          label={colDef.headerName}
          loadedModel={loadedModel}
          removeFilter={this.removeFilter}
          submitFilter={this.onSubmit}
        >
          {(intl) => {
            const locale = utils.DateUtils.translateLocaleToDateFnsLocale(
              intl.locale
            );
            const dateFormat = utils.DateUtils.getLocaleDateFnsFormatPattern(
              intl.locale
            );
            return (
              <div>
                <div className={styles.dlGridDateRangeFilterInputWrapper}>
                  <span>
                    <FormattedMessage {...I18N.FROM} />
                  </span>
                  <div className={styles.dlInnerDataPickerWrapper}>
                    <DatePicker
                      className={styles.dlGridDateRangeFilterInput}
                      onChange={this.handleChangeStart}
                      onKeyDown={this.handleKeyDown}
                      onInputClick={this.handleInputClickStart}
                      dateFormat={dateFormat}
                      defaultValue={STATIC_EMPTY_ARRAY}
                      disabledKeyboardNavigation
                      dropdownMode={'select'}
                      locale={locale}
                      open={startOpen}
                      peekNextMonth
                      placeholderText={utils.TranslateUtils.translate(
                        I18N.START_PLACEHOLDER,
                        intl
                      )}
                      preventOpenOnFocus
                      ref={(elem) => {
                        this.elemStart = elem;
                      }}
                      selected={startDate}
                      selectsEnd
                      selectsStart
                      scrollableYearDropdown
                      showYearDropdown
                      showMonthDropdown
                      startDate={startDate}
                      endDate={endDate}
                      tabIndex={0}
                      utc
                    />
                  </div>
                  <span>
                    <FormattedMessage {...I18N.TO} />
                  </span>
                  <div className={styles.dlInnerDataPickerWrapper}>
                    <DatePicker
                      className={styles.dlGridDateRangeFilterInput}
                      onChange={this.handleChangeEnd}
                      onKeyDown={this.handleKeyDown}
                      onInputClick={this.handleInputClickEnd}
                      dateFormat={dateFormat}
                      defaultValue={STATIC_EMPTY_ARRAY}
                      disabledKeyboardNavigation
                      dropdownMode={'select'}
                      locale={locale}
                      open={endOpen}
                      peekNextMonth
                      placeholderText={utils.TranslateUtils.translate(
                        I18N.END_PLACEHOLDER,
                        intl
                      )}
                      preventOpenOnFocus
                      ref={(elem) => {
                        this.elemEnd = elem;
                      }}
                      selected={endDate}
                      selectsEnd
                      selectsStart
                      scrollableYearDropdown
                      showYearDropdown
                      showMonthDropdown
                      startDate={startDate}
                      endDate={endDate}
                      tabIndex={0}
                      utc
                    />
                  </div>
                </div>
                <div className={styles.dlDayDifferenceWrapper}>
                  <span
                    className={classNames({
                      [styles.dlDayDifferenceBad]: dateDifference < 0,
                      [styles.dlDayDifferenceGood]: dateDifference >= 0,
                    })}
                  >
                    <FormattedMessage
                      {...I18N.TOTAL_TEXT}
                      values={{dateDifference}}
                    />
                  </span>
                </div>
              </div>
            );
          }}
        </GridFilterWrapper>
      </div>
    );
  }
}

GridDateRangeFilter.displayName = 'GridDateRangeFilter';

GridDateRangeFilter.propTypes = {
  dateOnly: PropTypes.bool,
  colDef: PropTypes.objectOf(PropTypes.any).isRequired,
  filterChangedCallback: PropTypes.func.isRequired,
  filterModifiedCallback: PropTypes.func.isRequired,
  valueGetter: PropTypes.func.isRequired,
};

GridDateRangeFilter.defaultProps = {
  dateOnly: false,
};

export default GridDateRangeFilter;
